import { useSelector } from "@tanstack/react-store";
import { LoadingWrapper } from "@vapor/react-contrib-tax-compliance";
import Typography from "@vapor/react-extended/ExtendedTypography";
import { Box, List, ListItem, Stack, useTheme } from "@vapor/react-material";
import dayjs from "dayjs";
import { type ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetFulfilmentTaxPayers } from "../../../../../core/api/use-get-fulfilment-tax-payers/useGetFulfilmentTaxPayers";
import type { FulfilmentDto } from "../../../../../core/api/use-get-fulfilments/FulfilmentDto";
import type { TaxPayerActivityCountersDto } from "../../../../../core/api/use-get-fulfilments/TaxPayerActivityCountersDto";
import type { FulfilmentFilterDto } from "../../../../../core/dtos/FulfilmentFilterDto";
import { sessionStoreSlice } from "../../../../../store/sessionStoreSlice";
import { store } from "../../../../../store/store";
import { FulfimentActivityFilter } from "../fulfilment-activity-filter/FulfimentActivityFilter";
import { FulfilmentTaxPayerListFilter } from "../fulfilment-tax-payer-list-filter/FulfilmentTaxPayerListFilter";
import { FulfilmentTaxPayerListItem } from "../fulfilment-tax-payer-list-item/FulfilmentTaxPayerListItem";

const defaultActivityFilter: FulfilmentFilterDto = {
    linked: false,
    closed: false,
    completed: false,
    pending: false,
};

const EmptyData = (): ReactElement => {
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <Box
            sx={{
                textAlign: "center",
                padding: 4,
            }}
        >
            <Typography
                color={theme.palette.primary.textTitleColor}
                variant="h5"
            >
                {t("views.home.components.fulfilment_taxPayer_list.no_data")}
            </Typography>
        </Box>
    );
};

export interface FulfilmentTaxPayerListProps {
    selectedFulfilment: FulfilmentDto;
    taxPayerActivityCounters: TaxPayerActivityCountersDto;
}

export const FulfilmentTaxPayerList = ({
    selectedFulfilment,
    taxPayerActivityCounters,
}: FulfilmentTaxPayerListProps): ReactElement => {
    const { t } = useTranslation();

    const taxPayerWorkspaceIdSearchFilter = useSelector(
        store,
        (state) => state.fulfilmentSearchFilters.taxPayerWorkspaceId,
    );

    const [selectedActivityFilter, setSelectedActivityFilter] =
        useState<FulfilmentFilterDto>(defaultActivityFilter);
    // Pre-seeded with the overview's tax payer filter: opening a fulfilment
    // from a filtered list must show only that tax payer. The user can still
    // clear it to see the fulfilment's other tax payers.
    const [taxPayerFilterWorkspaceId, setTaxPayerFilterWorkspaceId] = useState<
        string | null
    >(taxPayerWorkspaceIdSearchFilter ?? null);

    const {
        data: taxPayers,
        isFetching: isLoading,
        isError,
    } = useGetFulfilmentTaxPayers({
        levyId: selectedFulfilment.levyId,
        year: dayjs(selectedFulfilment.deadline).year(),
    });

    const selectedTaxPayerFilter = useMemo(
        () =>
            taxPayers.find(
                (taxPayer) =>
                    taxPayer.workspaceId === taxPayerFilterWorkspaceId,
            ) ?? null,
        [taxPayers, taxPayerFilterWorkspaceId],
    );

    // Safety net: a fulfilment exists server-side only while it has at least
    // one associated tax payer. If the list fetch fails (e.g. the fulfilment
    // was deleted by dissociating its last tax payer, possibly from another
    // session), close the drawer instead of showing a stale, clickable list
    // that would lead to further errors.
    useEffect(() => {
        if (isError) {
            sessionStoreSlice.updateValues({ selectedFulfilment: undefined });
        }
    }, [isError]);

    const filteredTaxPayers = useMemo(() => {
        const selectedFilters = Object.keys(selectedActivityFilter).filter(
            (key) => selectedActivityFilter[key as keyof FulfilmentFilterDto],
        );

        return taxPayers.filter((taxPayer) => {
            const taxPayerFilter = taxPayerFilterWorkspaceId
                ? taxPayer.workspaceId === taxPayerFilterWorkspaceId
                : true;

            if (selectedFilters.length === 0) return taxPayerFilter;

            return (
                selectedFilters.includes(taxPayer.status.toLowerCase()) &&
                taxPayerFilter
            );
        });
    }, [selectedActivityFilter, taxPayerFilterWorkspaceId, taxPayers]);

    return (
        <Stack direction="column" spacing={4}>
            <Stack direction="column" spacing={2} padding={2}>
                <FulfilmentTaxPayerListFilter
                    taxPayers={taxPayers}
                    value={selectedTaxPayerFilter}
                    onFilterChange={(taxPayer) =>
                        setTaxPayerFilterWorkspaceId(
                            taxPayer?.workspaceId ?? null,
                        )
                    }
                />
                <FulfimentActivityFilter
                    filters={selectedActivityFilter}
                    hideTotalActivityCounter
                    onClickFilter={(filter) =>
                        setSelectedActivityFilter(filter)
                    }
                    taxPayerActivityCounters={taxPayerActivityCounters}
                />
            </Stack>
            <LoadingWrapper loading={isLoading} variant="skeleton-list">
                {taxPayers && taxPayers.length > 0 ? (
                    <List disablePadding style={{ marginTop: 0 }}>
                        {filteredTaxPayers.length > 0 ? (
                            filteredTaxPayers.map((taxPayer) => (
                                <ListItem
                                    key={taxPayer.workspaceId}
                                    disableGutters
                                    sx={{
                                        "&.MuiListItem-root": {
                                            padding: 0,
                                            border: "1px solid #B9C8D0",
                                            borderLeft: "1px solid transparent",
                                            borderRight:
                                                "1px solid transparent",
                                        },
                                        "&.MuiListItem-root:hover": {
                                            borderLeft: "1px solid transparent",
                                            borderRight:
                                                "1px solid transparent",
                                        },
                                    }}
                                >
                                    <FulfilmentTaxPayerListItem
                                        selectedFulfilment={selectedFulfilment}
                                        taxPayer={taxPayer}
                                    />
                                </ListItem>
                            ))
                        ) : (
                            <Box sx={{ px: 2, py: 1 }}>
                                <Typography>
                                    {t(
                                        "views.home.components.fulfilment_taxPayer_list.no_tenant",
                                    )}
                                </Typography>
                            </Box>
                        )}
                    </List>
                ) : (
                    <EmptyData />
                )}
            </LoadingWrapper>
        </Stack>
    );
};
